import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { X, XCircle } from "lucide-react";
import {
  getBillingStatus,
  getBillingHistory,
  createCheckout,
  cancelSubscription,
} from "../lib/billing-api.js";
import { pollUntil } from "../lib/polling.js";
import { SubscriptionStatusCard } from "../components/SubscriptionStatusCard.js";
import { PaymentHistoryList } from "../components/PaymentHistoryList.js";
import { CancelConfirmDialog } from "../components/CancelConfirmDialog.js";
import { formatUserErrorMessage } from "../lib/error-formatter.js";

export function SubscriptionScreen(): React.ReactElement {
  const queryClient = useQueryClient();
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { data: billingStatus, isLoading: isLoadingStatus } = useQuery({
    queryKey: ["billingStatus"],
    queryFn: getBillingStatus,
  });

  const { data: historyData, isLoading: isLoadingHistory } = useQuery({
    queryKey: ["billingHistory"],
    queryFn: getBillingHistory,
  });

  const checkoutMutation = useMutation({
    mutationFn: () => createCheckout("premium"),
    onSuccess: async (data) => {
      setErrorMessage(null);
      const paymentUrl = data.paymentUrl;

      // Open Tribute checkout page
      if (window.Telegram?.WebApp?.openLink) {
        window.Telegram.WebApp.openLink(paymentUrl);
      } else {
        window.open(paymentUrl, "_blank");
      }

      // Start polling for payment completion
      setIsPolling(true);
      const updatedStatus = await pollUntil(
        getBillingStatus,
        (res) => res.isActive === true,
        2000,
        30000,
      );

      setIsPolling(false);
      if (updatedStatus?.isActive) {
        queryClient.invalidateQueries({ queryKey: ["billingStatus"] });
        queryClient.invalidateQueries({ queryKey: ["billingHistory"] });
      }
    },
    onError: (err) => {
      setErrorMessage(formatUserErrorMessage(err, "Не удалось сформировать счет на оплату"));
    },
  });

  const cancelMutation = useMutation({
    mutationFn: () => cancelSubscription(),
    onSuccess: () => {
      setIsCancelModalOpen(false);
      queryClient.invalidateQueries({ queryKey: ["billingStatus"] });
      queryClient.invalidateQueries({ queryKey: ["billingHistory"] });
    },
    onError: (err) => {
      setErrorMessage(formatUserErrorMessage(err, "Не удалось отменить подписку"));
    },
  });

  const isActive = billingStatus?.isActive ?? false;
  const hasSubscription = Boolean(billingStatus?.subscription);
  const isCancelled =
    Boolean(billingStatus?.subscription?.cancelAtPeriodEnd) ||
    billingStatus?.subscription?.status === "cancelled";

  return (
    <div
      data-testid="subscription-screen"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "16px",
        flex: 1,
        height: "100%",
        minHeight: 0,
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          padding: "4px 94px 4px 4px",
          minHeight: "56px",
          flexShrink: 0,
          boxSizing: "border-box",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
          <h1
            style={{
              margin: 0,
              fontSize: "22px",
              lineHeight: "26px",
              fontWeight: 700,
              color: "var(--color-graphite)",
              letterSpacing: "-0.02em",
            }}
          >
            Моя подписка
          </h1>
          <p
            style={{
              margin: "2px 0 0 0",
              fontSize: "13px",
              lineHeight: "16px",
              color: "var(--color-slate)",
            }}
          >
            Текущий тариф и история оплаты
          </p>
        </div>
      </div>

      {/* Subscription Status Card */}
      <SubscriptionStatusCard status={billingStatus} isLoading={isLoadingStatus} showDetails={true} />

      {/* Error Message if any */}
      {errorMessage && (
        <div
          data-testid="subscription-error"
          style={{
            padding: "12px 16px",
            backgroundColor: "var(--color-destructive-bg)",
            color: "var(--color-destructive)",
            borderRadius: "var(--radius-input)",
            fontSize: "14px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            flexShrink: 0,
          }}
        >
          <XCircle size={18} />
          {errorMessage}
        </div>
      )}

      {/* Payment History Section (Fills available space and scrolls internally) */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          gap: "8px",
        }}
      >
        <h2
          style={{
            margin: "0 0 4px 4px",
            fontSize: "18px",
            fontWeight: 600,
            color: "var(--color-graphite)",
            flexShrink: 0,
          }}
        >
          История платежей
        </h2>
        <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
          <PaymentHistoryList
            payments={historyData?.payments || []}
            isLoading={isLoadingHistory}
          />
        </div>
      </div>

      {/* Action Button (Docked at bottom of screen) */}
      <div style={{ flexShrink: 0, marginTop: "auto", paddingTop: "4px" }}>
        {!hasSubscription || !isActive ? (
          <button
            data-testid="checkout-btn"
            disabled={checkoutMutation.isPending || isPolling}
            onClick={() => checkoutMutation.mutate()}
            style={{
              width: "100%",
              padding: "14px",
              backgroundColor: "var(--color-muted-gold)",
              color: "var(--color-paper)",
              borderRadius: "var(--radius-button)",
              border: "none",
              fontSize: "14px",
              fontWeight: 600,
              cursor: checkoutMutation.isPending || isPolling ? "not-allowed" : "pointer",
              boxShadow: "var(--shadow-floating)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              boxSizing: "border-box",
            }}
          >
            {isPolling ? (
              <>
                <div
                  style={{
                    width: "16px",
                    height: "16px",
                    border: "2px solid #ffffff",
                    borderTopColor: "transparent",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                  }}
                />
                Ожидание подтверждения оплаты...
              </>
            ) : checkoutMutation.isPending ? (
              "Формирование счета..."
            ) : (
              "Оформить за 199 ₽/мес."
            )}
          </button>
        ) : isCancelled ? null : (
          <button
            type="button"
            data-testid="cancel-subscription-btn"
            onClick={() => setIsCancelModalOpen(true)}
            style={{
              width: "100%",
              padding: "14px",
              backgroundColor: "var(--color-paper)",
              color: "var(--color-destructive)",
              borderRadius: "var(--radius-button)",
              border: "1px solid var(--color-frost-border)",
              fontSize: "14px",
              fontWeight: 600,
              cursor: "pointer",
              boxShadow: "var(--shadow-card)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              boxSizing: "border-box",
            }}
          >
            <X size={16} />
            Отменить подписку
          </button>
        )}
      </div>

      {/* Cancel Confirmation Dialog */}
      <CancelConfirmDialog
        isOpen={isCancelModalOpen}
        isCancelling={cancelMutation.isPending}
        onConfirm={() => cancelMutation.mutate()}
        onCancel={() => setIsCancelModalOpen(false)}
      />
    </div>
  );
}

export default SubscriptionScreen;
